import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Paper, Button } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

declare global {
  interface Window {
    Razorpay: any;
  }
}

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      return resolve(true);
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const ExternalPaymentGateway: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const type = queryParams.get('type') || 'external';
  const recordId = queryParams.get('recordId');
  const amount = queryParams.get('amount');
  const name = queryParams.get('name') || '';
  const email = queryParams.get('email') || '';
  const contact = queryParams.get('contact') || '';
  const returnUrl = queryParams.get('returnUrl');
  const apiUrl = queryParams.get('apiUrl'); // e.g. http://localhost:5000

  const [status, setStatus] = useState<'initializing' | 'processing' | 'success' | 'failed'>('initializing');
  const [errorMessage, setErrorMessage] = useState('');
  
  const hasTriggered = useRef(false);

  useEffect(() => {
    if (hasTriggered.current) return;

    if (!recordId || !amount || !apiUrl) {
      setStatus('failed');
      setErrorMessage('Missing required payment parameters.');
      return;
    }

    const initiatePayment = async () => {
      hasTriggered.current = true;
      setStatus('processing');

      const isScriptLoaded = await loadRazorpayScript();
      if (!isScriptLoaded) {
        setStatus('failed');
        setErrorMessage('Failed to load Razorpay SDK. Please check your internet connection.');
        return;
      }

      try {
        // 1. Create order on the External Backend (Peptides)
        const orderResponse = await fetch(`${apiUrl}/api/payment/create-order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount }),
        });

        const orderData = await orderResponse.json();

        if (!orderData.success) {
          throw new Error(orderData.message || 'Failed to create order');
        }

        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_TFh88f2pBV2hSz', // Fallback to test key for dev
          amount: orderData.order.amount,
          currency: orderData.order.currency,
          name: 'Octacrest Corporate Pvt. Ltd.',
          description: `Payment for ${type} registration`,
          order_id: orderData.order.id,
          handler: async (response: any) => {
            try {
              setStatus('processing');
              // 2. Verify payment on the External Backend (Peptides)
              const verifyResponse = await fetch(`${apiUrl}/api/payment/verify-payment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  recordId: recordId,
                  type: type
                }),
              });

              const verifyData = await verifyResponse.json();

              if (verifyData.success) {
                setStatus('success');
                if (returnUrl) {
                  setTimeout(() => {
                    // Append payment success parameters
                    const separator = returnUrl.includes('?') ? '&' : '?';
                    window.location.href = `${returnUrl}${separator}payment=success&recordId=${recordId}&paymentId=${response.razorpay_payment_id}`;
                  }, 3000);
                }
              } else {
                throw new Error(verifyData.message || 'Payment verification failed');
              }
            } catch (err: any) {
              console.error('Verification error:', err);
              setStatus('failed');
              setErrorMessage(err.message || 'Payment verification failed.');
            }
          },
          prefill: {
            name: name,
            email: email,
            contact: contact,
          },
          theme: {
            color: '#b89c6b',
          },
          modal: {
            ondismiss: function() {
              if (status !== 'success') {
                setStatus('failed');
                setErrorMessage('Payment cancelled by user.');
              }
            }
          }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response: any) {
          setStatus('failed');
          setErrorMessage(response.error.description || 'Payment failed');
        });
        rzp.open();

      } catch (err: any) {
        console.error('Payment initiation error:', err);
        setStatus('failed');
        setErrorMessage(err.message || 'An error occurred while initiating payment.');
      }
    };

    initiatePayment();
  }, [apiUrl, amount, recordId, type, name, email, contact, returnUrl, status]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb', p: 3 }}>
      <Paper elevation={4} sx={{ p: 5, maxWidth: 500, width: '100%', borderRadius: 3, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          {status === 'success' && <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60 }} />}
          {status === 'failed' && <ErrorOutlineIcon color="error" sx={{ fontSize: 60 }} />}
          {(status === 'initializing' || status === 'processing') && <CircularProgress size={60} sx={{ color: '#b89c6b' }} />}
        </Box>

        <Typography variant="h5" fontWeight={700} gutterBottom>
          {status === 'success' && 'Payment Successful!'}
          {status === 'failed' && 'Payment Failed'}
          {(status === 'initializing' || status === 'processing') && 'Secure Payment Gateway'}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {status === 'success' && 'Your registration has been completed securely. Redirecting you back...'}
          {status === 'failed' && errorMessage}
          {(status === 'initializing' || status === 'processing') && 'Please do not close or refresh this window while we securely process your transaction through Octacrest Corporate.'}
        </Typography>

        {status === 'failed' && returnUrl && (
          <Button 
            variant="contained" 
            fullWidth 
            onClick={() => {
                const separator = returnUrl.includes('?') ? '&' : '?';
                window.location.href = `${returnUrl}${separator}payment=failed`;
            }}
            sx={{ backgroundColor: '#b89c6b', '&:hover': { backgroundColor: '#a68c60' }, py: 1.5 }}
          >
            Return to Website
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default ExternalPaymentGateway;
